/** A tenant membership reference, never resolved by name or by global user id. */
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { unwrap } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { EntityPreview } from "@/shared/components/entity-preview";
import { ErrorState, LoadingState } from "@/shared/components/page";
import { Button } from "@/shared/ui/button";

/** Workflow context can expose existing commands, but cannot confer permission to execute them. */
export type PersonReferenceContext =
  | {
      kind: "work-order";
      workOrderId: string;
      assignmentRole?: string;
      onManageAssignment?: () => void;
    }
  | {
      kind: "member";
      onEdit: () => void;
    };

function PersonPreview({
  id,
  name,
  context,
  close,
}: {
  id: string;
  name: string;
  context?: PersonReferenceContext;
  close: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const allowed = can("identity.user.view");
  const member = useQuery({
    queryKey: ["members", id],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/identity/members/{member_id}", {
          params: { path: { member_id: id } },
        }),
      ),
    enabled: allowed,
    staleTime: 30_000,
  });
  const teams = useQuery({
    queryKey: ["teams"],
    queryFn: async () => unwrap(await api.GET("/api/v1/identity/teams")),
    enabled: allowed && member.isSuccess,
    staleTime: 30_000,
  });
  const canWork = can("workorders.workorder.view_all") || can("workorders.workorder.view_assigned");
  return (
    <div className="space-y-3">
      <div>
        <p className="font-semibold">{name}</p>
        {allowed && member.data?.user.display_name && member.data.user.display_name !== name ? (
          <p className="text-xs text-muted-foreground">
            {t("entityPreview.currentName")}: {member.data.user.display_name}
          </p>
        ) : null}
      </div>
      {allowed ? (
        member.isPending ? (
          <LoadingState className="justify-start p-0 text-sm" />
        ) : member.isError ? (
          <ErrorState error={member.error} onRetry={() => void member.refetch()} />
        ) : (
          <div className="space-y-1 border-y py-3 text-sm">
            <p>{member.data.job_title || t("entityPreview.noJobTitle")}</p>
            <p>{t(`members.status.${member.data.status}`)}</p>
            {member.data.skills.length ? <p>{member.data.skills.join(" · ")}</p> : null}
            {teams.isPending ? (
              <LoadingState className="justify-start p-0 text-sm" />
            ) : teams.isError ? (
              <ErrorState error={teams.error} onRetry={() => void teams.refetch()} />
            ) : (
              <p>
                {teams.data
                  .filter((team) => team.is_active && team.member_ids.includes(id))
                  .map((team) => team.name)
                  .join(" · ") || t("entityPreview.noTeam")}
              </p>
            )}
          </div>
        )
      ) : (
        <p className="text-sm text-muted-foreground">{t("entityPreview.restrictedPerson")}</p>
      )}
      {context?.kind === "work-order" && context.assignmentRole ? (
        <p className="text-sm">
          {t("entityPreview.onThisWorkOrder")}:{" "}
          {t(`entityPreview.assignmentRole.${context.assignmentRole}`)}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {context?.kind === "work-order" && canWork ? (
          <Button asChild variant="outline" size="sm">
            <Link to="/work-orders/$woId" params={{ woId: context.workOrderId }}>
              {t("entityPreview.openWorkOrder")}
            </Link>
          </Button>
        ) : null}
        {context?.kind === "work-order" &&
        context.onManageAssignment &&
        can("workorders.workorder.plan") &&
        allowed ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              close();
              context.onManageAssignment?.();
            }}
          >
            {t("workOrders.editAssignees")}
          </Button>
        ) : null}
        {context?.kind === "member" && can("identity.user.manage") ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              close();
              context.onEdit();
            }}
          >
            {t("app.edit")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/** Preserve recorded names and provide context-only previews when member details are restricted. */
export function PersonReference({
  id,
  name,
  context,
}: {
  id: string | null | undefined;
  name: string;
  context?: PersonReferenceContext;
}): ReactElement {
  if (!id) return <span>{name}</span>;
  return (
    <EntityPreview
      label={name}
      preview={(close) => <PersonPreview id={id} name={name} context={context} close={close} />}
    >
      <span className="min-w-0 [overflow-wrap:anywhere]">{name}</span>
    </EntityPreview>
  );
}
